
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface PortfolioInquiryProps {
  formUrl: string;
}

export default function PortfolioInquiry({ formUrl }: PortfolioInquiryProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value);
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) throw new Error('Submission failed');

      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', service: '', budget: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left – Text */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F2780C]/10 rounded-full mb-4">
              <i className="ri-rocket-line text-[#F2780C] text-sm"></i>
              <span className="text-[10px] sm:text-xs font-bold text-[#F2780C] uppercase tracking-wider">Start Your Project</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0D0D0D] leading-tight mb-4">
              Ready to Build Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">
                Next Success Story?
              </span>
            </h2>
            <p className="text-sm sm:text-base text-gray-500 leading-relaxed mb-8">
              Every project in our portfolio started with a conversation. Tell us about your brand, your goals, and your market — and we will develop a strategic communication system tailored to your needs.
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-timer-line', color: '#038C7F', title: '24-Hour Response', desc: 'We respond to every inquiry within one business day.' },
                { icon: 'ri-shield-check-line', color: '#F2780C', title: 'Free Consultation', desc: 'Initial strategy session at no cost or obligation.' },
                { icon: 'ri-global-line', color: '#038C7F', title: 'East Africa Coverage', desc: 'Serving Uganda, Kenya, Tanzania, Rwanda, and South Sudan.' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}12` }}>
                    <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[#0D0D0D]">{item.title}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right – Form */}
          <div className="bg-gray-50 rounded-2xl p-6 sm:p-8">
            <form onSubmit={handleSubmit} data-readdy-form id="portfolio-inquiry-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pf-name" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    id="pf-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="pf-email" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email *</label>
                  <input
                    type="email"
                    id="pf-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm transition-all"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pf-company" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Company</label>
                  <input
                    type="text"
                    id="pf-company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm transition-all"
                    placeholder="Your Company"
                  />
                </div>
                <div>
                  <label htmlFor="pf-service" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Service Interest *</label>
                  <select
                    id="pf-service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm cursor-pointer transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="Brand Development">Brand Development &amp; Positioning</option>
                    <option value="Digital Communication">Digital Communication</option>
                    <option value="Film & Media">Film, Media &amp; PR</option>
                    <option value="Outdoor Advertising">Outdoor Advertising</option>
                    <option value="Garment Manufacturing">Garment Manufacturing</option>
                    <option value="Content Strategy">Content Strategy</option>
                    <option value="Multiple Services">Multiple Services</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="pf-budget" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Estimated Budget</label>
                <select
                  id="pf-budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm cursor-pointer transition-all"
                >
                  <option value="">Select budget range</option>
                  <option value="Under $2,000">Under $2,000</option>
                  <option value="$2,000 - $5,000">$2,000 - $5,000</option>
                  <option value="$5,000 - $15,000">$5,000 - $15,000</option>
                  <option value="$15,000 - $50,000">$15,000 - $50,000</option>
                  <option value="$50,000+">$50,000+</option>
                </select>
              </div>

              <div>
                <label htmlFor="pf-message" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Project Details *</label>
                <textarea
                  id="pf-message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm resize-none transition-all"
                  placeholder="Tell us about your project, goals, and timeline..."
                ></textarea>
                <div className="text-[10px] text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="group w-full px-6 py-3.5 bg-[#0D0D0D] text-white text-sm font-bold rounded-lg hover:bg-[#038C7F] transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Inquiry</span>
                    <div className="w-7 h-7 bg-white/15 rounded-lg flex items-center justify-center group-hover:bg-white/25 transition-colors">
                      <i className="ri-send-plane-line text-sm"></i>
                    </div>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-xs sm:text-sm">
                  <i className="ri-checkbox-circle-line text-lg text-green-600 flex-shrink-0"></i>
                  <span>Thank you! We&apos;ll get back to you within 24 hours.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm">
                  <i className="ri-error-warning-line text-lg text-red-600 flex-shrink-0"></i>
                  <span>Something went wrong. Please try again.</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Mini Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 pt-8 border-t border-gray-100">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="https://asili.co.ug/visuals/assets/img/logo-AsiliVisuals.png" alt="Asili Visuals" className="h-8 w-auto" />
            <span className="text-xs text-gray-400">&copy; 2026 Asili Visuals. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-gray-400 hover:text-[#038C7F] transition-colors cursor-pointer">Home</Link>
            <Link to="/portfolio" className="text-xs text-gray-400 hover:text-[#038C7F] transition-colors cursor-pointer">Portfolio</Link>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="text-xs text-gray-400 hover:text-[#038C7F] transition-colors cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </div>
    </section>
  );
}
